import {
  DropdownInput,
  DropdownOption,
  FlexBox,
  JoonUIColor,
  Modal,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import CustomDateSelectorModal, {
  useCustomDateSelectorModalStore,
} from "./CustomDateSelectorModal"
import TransactionTable from "./TransactionTable"
import { useCoinTransactionsModalStore } from "./useTransactionsModalStore"
import { periodDropdownOptions } from "../../../constants"
import {
  useCoinCulumativeStatsQuery,
  useCoinTransactionsQuery,
} from "../../../networking/queries"

const CoinTransactionsModal = () => {
  const { isOpen, onClose, child, selectedPeriod, setSelectedPeriod } =
    useCoinTransactionsModalStore()
  const { onOpen: openCustomDateModal } = useCustomDateSelectorModalStore()
  const { t } = useTranslation()

  const { data: coins } = useCoinTransactionsQuery()
  const { data: stats } = useCoinCulumativeStatsQuery()

  if (!child) return null

  const periodLabel = (value?: string | number | boolean | null) => {
    switch (value) {
      case "all_time":
        return t("CT_PERIOD_ALL_TIME")
      case "this_week":
        return t("CT_PERIOD_THIS_WEEK")
      case "this_month":
        return t("CT_PERIOD_THIS_MONTH")
      case "custom":
        return t("CT_PERIOD_CUSTOM")
      default:
        return ""
    }
  }

  const localizedPeriodOptions = periodDropdownOptions.map((option) => ({
    ...option,
    label: periodLabel(option.value),
  }))

  // For a chosen custom range the label is a formatted date range, so keep it
  // as-is; otherwise translate the known period label.
  const selectedPeriodLabel =
    selectedPeriod?.value === "custom"
      ? selectedPeriod.label
      : periodLabel(selectedPeriod?.value)

  const onClickPeriodOption = (option: DropdownOption) => {
    if (option.value === "custom") openCustomDateModal()
    else setSelectedPeriod(option)
  }

  const onSubmitCustomDateModal = (startDate: Date, endDate: Date) => {
    setSelectedPeriod({
      label: `${dayjs(startDate).format("MMM D, YYYY")} - ${dayjs(
        endDate
      ).format("MMM D, YYYY")}`,
      value: "custom",
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} displayCloseIcon animate>
      <FlexBox
        style={{ width: "min(700px, 100vw)", padding: SPACING.space6 }}
        gap={SPACING.space4}
      >
        <FlexBox direction="column" gap={SPACING.space4}>
          <FlexBox align="center" justify="center" wrap={false}>
            <img
              src="/images/icons/coin-icon.png"
              alt="coin"
              style={{ width: "20px", marginRight: "12px" }}
            />
            <Typography variant="h2">
              {t("CT_CHILD_COINS", { name: child?.name })}
            </Typography>
          </FlexBox>
          <FlexBox justify="center" align="center">
            <DropdownInput
              name="period"
              options={localizedPeriodOptions}
              label={selectedPeriodLabel}
              style={{ minWidth: "200px" }}
              onSelect={onClickPeriodOption}
            />
          </FlexBox>
          <FlexBox justify="space-between" align="center" wrap={false}>
            <div style={{ margin: "0 auto" }}>
              <Typography
                variant="bodySmallBold"
                textAlign="left"
                color={JoonUIColor.text.secondary}
              >
                {t("CT_BALANCE_AVAILABLE")}
              </Typography>
              <Typography variant="h2" color={JoonUIColor.semantic.primary}>
                {child.coins.toLocaleString()}
              </Typography>
            </div>
            <div
              style={{
                borderLeft: "1px solid #ccc",
                height: "50px",
                width: "2px",
                margin: `0 ${SPACING.space4}`,
              }}
            />
            <FlexBox
              align="center"
              justify="space-around"
              style={{ maxWidth: "50%" }}
              wrap={false}
            >
              <div>
                <Typography
                  variant="bodySmallBold"
                  textAlign="left"
                  color={JoonUIColor.text.secondary}
                >
                  {t("CT_EARNED")}
                </Typography>
                <Typography variant="h2" color={JoonUIColor.semantic.primary}>
                  {stats?.coins_earned || "-"}
                </Typography>
              </div>
              <div>
                <Typography
                  variant="bodySmallBold"
                  textAlign="left"
                  color={JoonUIColor.text.secondary}
                >
                  {t("CT_SPENT")}
                </Typography>
                <Typography variant="h2" color={JoonUIColor.semantic.primary}>
                  {stats?.coins_spent || "-"}
                </Typography>
              </div>
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <hr />
        <TransactionTable transactions={coins} />
        <CustomDateSelectorModal onSubmit={onSubmitCustomDateModal} />
      </FlexBox>
    </Modal>
  )
}

export default CoinTransactionsModal
